export const IMAGE_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
export const FILE_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;

export const COURSE_IMAGE_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/svg+xml',
    'image/webp',
];

export const COURSE_ALLOWED_UPLOAD_TYPES = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/gif': ['.gif'],
    'image/svg+xml': ['.svg'],
    'image/webp': ['.webp'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
};

const MIME_ALIASES = {
    'image/jpg': 'image/jpeg',
};

const WORD_MIME_TYPES = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const EXCEL_MIME_TYPES = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const POWERPOINT_MIME_TYPES = [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const COURSE_UPLOAD_ACCEPT = '.png,.jpg,.jpeg,.gif,.svg,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';

export const COURSE_UPLOAD_HINT = 'Imágenes máx. 50 MB · documentos máx. 100 MB';

export const getFileExtension = (fileName = '') => {
    const dot = String(fileName).lastIndexOf('.');
    return dot === -1 ? '' : String(fileName).slice(dot).toLowerCase();
};

export const isImageAttachment = (mimeType) => COURSE_IMAGE_MIME_TYPES.includes(mimeType);

export const getAttachmentKind = (mimeType) => {
    if (isImageAttachment(mimeType)) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    if (WORD_MIME_TYPES.includes(mimeType)) return 'word';
    if (EXCEL_MIME_TYPES.includes(mimeType)) return 'excel';
    if (POWERPOINT_MIME_TYPES.includes(mimeType)) return 'powerpoint';
    return 'file';
};

export const ATTACHMENT_KIND_LABELS = {
    image: 'Imagen',
    pdf: 'PDF',
    word: 'Documento Word',
    excel: 'Hoja de cálculo',
    powerpoint: 'Presentación',
    file: 'Archivo',
};

export const resolveMimeType = (file) => {
    const reported = MIME_ALIASES[file?.type] || file?.type;
    if (reported && COURSE_ALLOWED_UPLOAD_TYPES[reported]) return reported;

    const extension = getFileExtension(file?.name);
    const match = Object.entries(COURSE_ALLOWED_UPLOAD_TYPES)
        .find(([, extensions]) => extensions.includes(extension));

    return match ? match[0] : (reported || '');
};

export const formatFileSize = (bytes) => {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) return '';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMb = (bytes) => Math.round(bytes / (1024 * 1024));

/**
 * @returns {{ valid: true, mimeType: string } | { valid: false, message: string }}
 */
export const validateCourseFile = (file) => {
    const mimeType = resolveMimeType(file);
    const extension = getFileExtension(file?.name);
    const allowedForMime = COURSE_ALLOWED_UPLOAD_TYPES[mimeType];
    const fileName = file?.name || 'archivo';

    if (!allowedForMime || !allowedForMime.includes(extension)) {
        return {
            valid: false,
            message: `«${fileName}» no es un formato permitido. Usa imágenes, PDF, Word, Excel o PowerPoint.`,
        };
    }

    if (!file.size) {
        return { valid: false, message: `«${fileName}» está vacío.` };
    }

    const isImage = isImageAttachment(mimeType);
    const maxBytes = isImage ? IMAGE_UPLOAD_MAX_BYTES : FILE_UPLOAD_MAX_BYTES;

    if (file.size > maxBytes) {
        const label = isImage ? 'La imagen' : 'El archivo';
        return {
            valid: false,
            message: `${label} «${fileName}» no puede superar ${formatMb(maxBytes)} MB.`,
        };
    }

    return { valid: true, mimeType };
};

export const asAttachmentList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return [value];
};

export const canPreviewInBrowser = (mimeType) => (
    isImageAttachment(mimeType) || mimeType === 'application/pdf'
);
